/**
 * Created by Alan Yang on 5/08/2019.
 * Description:
 * ManageScreensComponent, the grid definition logic is mainly in setAgGridColumns method.
 *
 * ------ maintenance history ------
 * update by Abner Sui on 06/11/2019
 * Change aggrid CellStyle for edit/delete button
 * update by Ella Ma on 09/05/2019 upgrade grid
 */
import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { ColDef, GridOptions } from 'ag-grid-community';
import { DeviceDetectorService } from 'ngx-device-detector';

import { AppState } from '../../redux';
import { StoreQuerierService } from '../../services/store-querier.service';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { AppConfig } from '../../tamalelibs/models/app-config.model';

import { ManageScrerensActionService } from './manage-screen.service';
import { ManageScreensActionButtonComponent } from './manage-screen-action-button.componment';
import { ManageScreensCopyButtonComponent } from './manage-screens-copy-button.component';
import { ManageScreensPrivateIconComponent } from './manage-screens-private-icon.component';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { SystemActionTypes, LoadScreens } from '../../tamalelibs/redux/actions/system.actions';
import { ManageScreensVisibleButtonComponent } from './manage-screens-visible-button.component';
import { businessConstants } from '../../constants/business.constants';
import { screensSelector } from '../../redux/reducers/system.reducer';
import { filter } from 'rxjs/operators';

export const DEFAULT_PAGE_SIZE = 9999;
@Component({
    selector: 'tam-manage-screens',
    templateUrl: './manage-screens.component.html',
    styleUrls: ['./manage-screens.component.scss']
})
export class ManageScreensComponent implements OnInit, OnDestroy {

    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    rowData: Array<any> = [];
    noDataStyle = 'none';
    reloading = false;

    private _selectedItemMap: Map<string, number> = new Map();
    private _currentUser: SystemUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi;
    private _isIPAD = false;

    constructor(
        private _deviceService: DeviceDetectorService,
        private _storeQuerier: StoreQuerierService,
        private _manageScrerensActionService: ManageScrerensActionService,
        private _store: Store<AppState>
    ) { }

    ngOnInit() {
        this._currentUser = this._storeQuerier.getCurrentUser();

        // hide copy/edit/delete button on iPad.
        this._isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();

        this._destroySubscriptions.push(
            this._store.pipe(
                select(screensSelector),
                filter(data => data),
            ).subscribe(result => {
                this._onManageScreensLoaded(result);
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),
        );

        // delete a row.
        this._manageScrerensActionService.removeItem$.subscribe((id: string) => {
            this._removeItem(id);
        });

        this.setAgGridColumns();

        this.setAgGridOptions();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onRowSelected(event) {
        const item = event.api.getSelectedNodes()[0];

        this._selectedItemMap.clear();
        this._selectedItemMap.set(item.data.id, item.rowIndex);
    }

    onGridReady(params) {
        this._gridApi = params.api;
    }

    private _actionMessageHandler(action) {
        if (action.type === SystemActionTypes.EDIT_SCREEN) {
            const selectedNote = action.payload;
            this._gridApi.forEachNode(function (node) {
                if (node.data && node.data.id === selectedNote.id) {
                    node.data.name = selectedNote.name;
                    node.data.description = selectedNote.description;
                    node.data.accessList = selectedNote.accessList;
                }
            });

            const params = {
                rowNodes: this._gridApi.getSelectedNodes(),
                force: true
            };
            this._gridApi.refreshCells(params);
        }
    }

    /**
     * setAgGridColumns, control cell styling and show/hide logic.
     * for the private icon, copy,edit,delete icon, uses ICellRendererAngularComp.
     * @private
     * @memberof ManageScreensComponent
     */
    private setAgGridColumns() {

        const cellStyleHandler = () => {
            return {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none'
            };
        };
        this.columnDefs = [
            {
                cellStyle: (params) => {
                    if (params.data.isPublic) {
                        return { 'display': 'none' };
                    } else {
                        return cellStyleHandler();
                    }
                },
                headerName: '',
                minWidth: 25,
                width: 25,
                maxWidth: 25,
                cellRenderer: ManageScreensPrivateIconComponent,
                menuTabs: []
            },
            {
                headerName: 'Name',
                valueGetter: 'data.name',
                colId: 'name',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                comparator: (a, b) => {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                cellStyle: (params) => {
                    if (params.data.id === AppConfig.allNotesScreenId
                        || params.data.id === AppConfig.myNotesScreenId
                        || params.data.id === businessConstants.activityScreenId) {
                        // show all notes/my notes as un-editable.
                        return Object.assign(cellStyleHandler(), { color: '#cccccc' });
                    } else {
                        return cellStyleHandler();
                    }
                },
                minWidth: 80,
                width: 288,
                sort: 'asc', // set sort by name asc by default
                menuTabs: [], // 'filterMenuTab' not working, diable it using [].
                field: 'data.name',
                filter: 'agTextColumnFilter'
            },
            {
                headerName: 'Description',
                valueGetter: 'data.description',
                colId: 'description',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                minWidth: 80,
                width: 576,
                comparator: (a, b) => {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                cellStyle: (params) => {
                    if (params.data.id === AppConfig.allNotesScreenId
                        || params.data.id === AppConfig.myNotesScreenId
                        || params.data.id === businessConstants.activityScreenId) {
                        // show all notes/my notes as un-editable.
                        return Object.assign(cellStyleHandler(), { color: '#cccccc' });
                    } else {
                        return cellStyleHandler();
                    }
                },
                menuTabs: [],
                field: 'data.description',
                filter: 'agTextColumnFilter'
            },
            {
                headerName: 'Type',
                valueGetter: (params) => {
                    let typeStr = '';
                    switch (params.data.type) {
                        case 'research':
                            typeStr = 'Research';
                            break;
                        case 'dashboard':
                            typeStr = 'Dashboard';
                            break;
                        case 'workflow':
                            typeStr = 'Workflow';
                            break;
                        case businessConstants.screenType.intralink:
                            typeStr = 'Intralinks';
                            break;
                        default:
                            typeStr = params.data.type;
                            break;
                    }
                    return typeStr;
                },
                colId: 'type',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                cellStyle: (params) => {
                    if (params.data.id === AppConfig.allNotesScreenId
                        || params.data.id === AppConfig.myNotesScreenId
                        || params.data.id === businessConstants.activityScreenId) {
                        // show all notes/my notes as un-editable.
                        return Object.assign(cellStyleHandler(), { color: '#cccccc' });
                    } else {
                        return cellStyleHandler();
                    }
                },
                width: 288,
                // suppressSorting: false,
                menuTabs: [],
                field: 'data.type',
            },
            {
                headerName: 'Author',
                valueGetter: 'data.author["long-name"]',
                colId: 'author',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                cellStyle: (params) => {
                    if (params.data.id === AppConfig.allNotesScreenId
                        || params.data.id === AppConfig.myNotesScreenId
                        || params.data.id === businessConstants.activityScreenId) {
                        // show all notes/my notes as un-editable.
                        return Object.assign(cellStyleHandler(), { color: '#cccccc' });
                    } else {
                        return cellStyleHandler();
                    }
                },
                minWidth: 144,
                width: 144,
                maxWidth: 144,
                menuTabs: [],
                field: 'data.author["long-name"]', // make sure it's not null.
            },
            {
                headerName: 'Show on Menu',
                headerComponentParams: {
                    template:
                        '<div class="ag-cell-label-container" role="presentation" style="width:100%;">' +
                        '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
                        '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="width:100%;display:flex;">' +
                        '    <span ref="eText" class="ag-header-cell-text" role="columnheader" style="flex:1;text-align:center;"></span>' +
                        '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>' +
                        '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
                        '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
                        '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
                        '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
                        '  </div>' +
                        '</div>'
                },
                valueGetter: 'data.visible',
                colId: 'visible',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                cellStyle: (params) => {
                    return Object.assign({}, cellStyleHandler(), {
                        'padding': '10px 0 0 0',
                        'margin': 0,
                        'height': '100%',
                        'text-align': 'center'
                    });
                },
                minWidth: 144,
                width: 144,
                maxWidth: 144,
                cellRenderer: ManageScreensVisibleButtonComponent,
                sortable: false,
                menuTabs: [],
            },
            {
                cellStyle: (params) => {
                    const type = params.data.type.toLowerCase();
                    // Types only have "Research", "Dashboard". research can't copy.
                    // At present workflow can't copy.
                    if (this._isIPAD || type === 'research'
                        || params.data.id === AppConfig.allNotesScreenId
                        || params.data.id === AppConfig.myNotesScreenId ||
                        type === 'workflow' || type === businessConstants.screenType.intralink) {
                        // My Notes and All Notes are not editable, delete-able, and copy-able. which is the only 2 created by server
                        return { 'display': 'none' };
                    } else if (this._currentUser.credential.publishPubConfiguration) {
                        // ppc user, show copy/edit/delete
                        return cellStyleHandler();
                    } else if (!params.data.author
                        || (params.data.author.id && params.data.author.id.indexOf(this._currentUser.id) < 0)) {
                        // user who is not PPC and the author, can't copy/edit/delete
                        return { 'display': 'none' };
                    } else {
                        return cellStyleHandler();
                    }
                },
                headerName: '',
                minWidth: 48,
                width: 48,
                maxWidth: 48,
                cellRenderer: ManageScreensCopyButtonComponent,
                sortable: false,
                menuTabs: []
            },
            {
                cellStyle: (params) => {
                    if (this._isIPAD || !params.data.deletable) {
                        return { 'display': 'none' };
                    } else {
                        return cellStyleHandler();
                    }
                },
                headerName: 'Edit/Delete',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                sortable: false,
                menuTabs: [],
                cellRenderer: ManageScreensActionButtonComponent,
                suppressMovable: true,
                headerClass: 'header-show',
            }
        ];
    }

    /**
     * ag-grid grid properties. Please check
     * https://www.ag-grid.com/javascript-grid-properties/
     *
     * @private
     * @memberof ManageScreensComponent
     */
    private setAgGridOptions() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true
            },
            suppressAutoSize: false,
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            getRowHeight: () => 40
        };
    }

    private _onManageScreensLoaded(response) {
        this.rowData = response;
    }

    private _removeItem(id: string) {
        const index = this.rowData.indexOf(this.rowData.find(data => (data.id === id)));
        this.rowData.splice(index, 1);

        if (this._gridApi) {
            this._gridApi.setRowData(this.rowData);
            this._gridApi.sizeColumnsToFit();
        }
    }
}
